import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Compliance } from 'src/app/modules/iep/iep-document/interfaces/compliance';
import { ReasonForDelay } from 'src/app/shared/components/reason-for-delay/interface/reason-for-delay';
import { AddReasonForDelayRequest } from 'src/app/shared/components/reason-for-delay/reason-for-delay-models/add-reason-for-delay.model';
import { handleError } from 'src/app/shared/functions/error-handler';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { ISedmsFormOutput } from 'src/app/shared/interfaces/form/sedms-form-output.model';
import { AppConfigService } from '../../../../services/app-config.service';
import { IAppConfig } from '../../../../shared/interfaces/app-config';
import { PreviousData } from '../../interfaces/previousdata-interface';
import {
  ReferenceValueResponse
} from '../../interfaces/reason-for-delay';
import { Evaluation } from '../interfaces/evaluation-checklist';

@Injectable({
  providedIn: 'root',
})
export class AssessmentService<
  TGet extends ISedmsFormOutput = ISedmsFormOutput,
  TSave extends ISedmsFormOutput = TGet,


> {
  config: IAppConfig;

  private assessmentDates: BehaviorSubject<Date[] | null> = new BehaviorSubject<
    Date[] | null
  >(null);
  assessmentDates$: Observable<Date[] | null> = this.assessmentDates.asObservable();

  private validLanguageData: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  validLanguageData$: Observable<boolean | null> = this.validLanguageData.asObservable();

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  public submit(request: TSave, type?: string): Observable<TGet> {
    void type;
    return this.http
      .put<TSave>(`${this.config.apiUrl}/Evaluations`, request)
      .pipe(
        map((res: TSave | null) => (res ? (res as unknown as TGet) : ({} as TGet))),
        catchError(handleError)
      );
  }
  public get(id: string, type?: string): Observable<TGet> {
    void type;
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http.get<TGet>(url).pipe(
      map((res: TGet) => (!res ? ({} as TGet) : res)),
      catchError(handleError)
    );

  }

  public getOccupationalTeacherChecklist(
    id: string,
    evaluationType: number
  ): Observable<ApiResponse<Evaluation>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EvaluationChecklist/Get/${encodeURIComponent(id)}/${encodeURIComponent(String(evaluationType))}`,
      this.config.apiUrl
    );
    return this.http
      .get<ApiResponse<Evaluation>>(url)
      .pipe(catchError(handleError));
  }

  public getTeacherChecklist(
    id: string,
    evaluationType: number
  ): Observable<ApiResponse<Evaluation>> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EvaluationChecklist/Get/${encodeURIComponent(id)}/${encodeURIComponent(String(evaluationType))}`,
      this.config.apiUrl
    );
    return this.http
      .get<ApiResponse<Evaluation>>(url)
      .pipe(
        map((res: ApiResponse<Evaluation>) =>
          !res
            ? ({
              isRequestSucessful: false,
              value: null,
            } as ApiResponse<Evaluation>)
            : res
        ),
        catchError(handleError)
      );
  }

  public submitPhysicalTeacherChecklist(
    request: Evaluation
  ): Observable<ApiResponse<string>> {
    return this.http
      .post<ApiResponse<string>>(
        `${this.config.apiUrl}/EvaluationChecklist/Add`,
        request
      )
      .pipe(catchError(handleError));
  }

  public Docsubmit(
    nycId: string,
    evaluationSk: string,
    evaluationType: number
  ): Observable<Uint8Array> {
    return this.http
      .get<Uint8Array>(
        `${this.config.apiUrl}/DocumentGen/EvalDocument?nycId=${encodeURIComponent(nycId)}&evaluationSk=${encodeURIComponent(evaluationSk)}&EvaluationType=${encodeURIComponent(String(evaluationType))}`
      )
      .pipe(catchError(handleError));
  }

  public updateSTACStatus(
    evaluationSk: string,
    isSubmit: boolean
  ): Observable<boolean> {
    return this.http
      .get<boolean>(
        `${this.config.apiUrl}/Evaluations/UpdateSTACStatus?evaluationSk=${encodeURIComponent(evaluationSk)}&isSubmit=${isSubmit}`
      )
      .pipe(catchError(handleError));
  }

  public getComplianceDate(id: string): Observable<Compliance> {
    return this.http
      .get<Compliance>(
        `${this.config.apiUrl}/Evaluations/GetComplianceDate?evaluationSk=${encodeURIComponent(id)}`,
      )
      .pipe(catchError(handleError));
  }

  public getReasonDelay(
    id: string,
    isEval: boolean,
  ): Observable<ReferenceValueResponse[]> {
    return this.http
      .get<
        ReferenceValueResponse[]
      >(`${this.config.apiUrl}/Evaluations/GetReasonforDelay?caseActionsk=${encodeURIComponent(id)}&isEval=${isEval}`)
      .pipe(catchError(handleError));
  }

  public submitReasonForDelay(
    evalutionSk: string,
    request: AddReasonForDelayRequest[],
  ): Observable<ApiResponse<ReasonForDelay[]>> {
    return this.http
      .post<ApiResponse<ReasonForDelay[]>>(
        `${this.config.apiUrl}/Evaluations/SaveReasonforDelay?evaluationSk=${encodeURIComponent(evalutionSk)}`,
        request,
      )
      .pipe(catchError(handleError));
  }

  // TODO: Not sure what the type should be, looks like the backend returns a `string` but the UI is expecting an object
  public getPreviousReport(id: string): Observable<PreviousData> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Evaluations/physical/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<PreviousData>(url)
      .pipe(catchError(handleError));
  }

  getAssessmentDates(data: Date[]): void {
    this.assessmentDates.next(data);
  }

  getLanguageValidation(data: boolean): void {
    this.validLanguageData.next(data);
  }
}
